import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Data, Router } from '@angular/router';
import * as moment from 'moment';
import { FilterMatchMode, FilterMetadata, FilterService, SortEvent, MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { BehaviorSubject, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AccountComponent } from '../account/account.component';
import { AuthService } from '../auth/auth.service';
import { AuthRoles } from '../auth/auth.types';
import { InfoReportService } from '../services/info-report.service';
import { LoaderService } from '../services/loader.service';
import { PreviousRouteService } from '../services/previous-route.service';
import { AppConstants } from '../shared/app.constants';
import { AppPermissions } from '../shared/app.permissions';
import { AppUtils } from '../shared/app.utils';
import { FilterModel } from '../shared/components/local-filter/filter-model';
import { LocalFilterComponent } from '../shared/components/local-filter/local-filter.component';
import { AuthorizationByStatusModel } from '../shared/models/authorization-model';

@Component({
  selector: 'ness-info-reports',
  templateUrl: './info-reports.component.html',
  providers: [DatePipe]
})
export class InfoReportsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(Table, { static: false }) dataTable: Table;
  cgicCode: string;
  eventStream$: BehaviorSubject<any> = new BehaviorSubject(undefined);
  filterModel: FilterModel;
  infoReports: ServerModels.IntelligenceReportViewModel[] = [];
  loading: boolean = false;
  loadingEndRow: number = AppConstants.dbPagingDefaultRows;
  loadingStartRow: number = 1;
  loadingStartRow$: BehaviorSubject<number> = new BehaviorSubject<number>(this.loadingStartRow);
  loadingEndRow$: BehaviorSubject<number> = new BehaviorSubject<number>(this.loadingEndRow);
  messageTemplate: string = 'Loading information reports...';
  nessAdmin: AuthRoles[] = ['NESS Administrator'];
  rows: number = AppConstants.defaultTableRows;
  selectedInfoReport$: Observable<ServerModels.ENESS_IntelligenceReports>;
  selectedResultId: string;
  showInfoReportDetailView: boolean;
  showRelatedResultDetailView: boolean;
  showSidebar: boolean = false;
  readonly storageKey: string = "infoReportFilterModel";
  private unsubscribe$: Subject<void> = new Subject<void>();

  private _canCreateIR: AuthorizationByStatusModel;
  get canCreate(): AuthorizationByStatusModel {
    this._canCreateIR = AppPermissions.canCreateIRs;
    return this._canCreateIR;
  }

  private _canEditIR: AuthorizationByStatusModel;
  get canEdit(): AuthorizationByStatusModel {
    this._canEditIR = AppPermissions.canEditIRs;
    return this._canEditIR;
  }

  private _canViewIR: AuthorizationByStatusModel;
  get canView(): AuthorizationByStatusModel {
    this._canViewIR = AppPermissions.canViewIRs;
    this._canViewIR.authOrgCode = this.authService.authOrgCode;
    return this._canViewIR;
  }

  set canView(ir: AuthorizationByStatusModel) {
    this._canViewIR = ir;
  }

  cols: any[] = [
    { field: 'IntelligenceTrackingNumber', header: 'IR Tracking #' },
    { field: 'CreatedBy', header: 'Created By' },
    { field: 'CreatedOn', header: 'Created Date' },
    { field: 'ModifiedBy', header: 'Last Updated By' },
    { field: 'ModifiedOn', header: 'Last Updated Date' },
    { field: 'Title', header: 'Title' },
    { field: 'Status', header: 'Status' },
    { field: 'IRPurposes', header: 'Purpose' },
    { field: 'PrimaryCrimeGunNumber.CrimeGunNumber', header: 'Crime Gun ID(s)' },
    { field: 'MostRecentTriageData', header: 'Triage Level; Date' },
    { field: 'AssignedCGICPersonnel', header: 'Assigned Personnel' },
    { field: 'DateSent', header: 'Date Sent' },
    { field: 'ENESS_XREF_SuccessStoriesResults', header: 'Related Results' }
  ]

  constructor(private authService: AuthService,
    private infoReportService: InfoReportService,
    private previousRouteService: PreviousRouteService,
    private router: Router,
    private datePipe: DatePipe,
    public hostElement: ElementRef,
    public filterService: FilterService,
    public loaderService: LoaderService,
    public messageService: MessageService) { }

  ngOnInit() {
    if (AccountComponent.userInfo.UserType.UserTypeName == 'ATFHQ') {
      this.cgicCode = 'all';
    } else {
      this.cgicCode = AccountComponent.userInfo.FieldDivision != null ? AccountComponent.userInfo.FieldDivision.ENESS_REF_NIBINCGICCodes.OrganizationCode : null;
    }
    this.filterModel = LocalFilterComponent.createFilterModel(this.storageKey, 30, 'Filter by creation date range');
    this.loading = true;
    this.getInfoReports(0, this.filterModel);
  }

  ngOnDestroy() {
    this.messageService.clear();
  }

  ngAfterViewInit(): void {
    this.filterService.register('date', (value:Date, filter:any): boolean => {
      if (filter === undefined || filter === null || filter.toString().trim() === '') {
        return true;
      }
      if (value === undefined || value === null) {
        return false;
      }
      let dateString = value.toString();
      if (dateString.indexOf('Z') === -1) {
        dateString = moment.parseZone(dateString).utc().format();
      }
      dateString = this.datePipe.transform(dateString, 'MM/dd/yyyy');
      return (dateString).search(filter) !== -1;
    });

    this.filterService.register('crimeGunNumber', (value: ServerModels.IRCrimeGunViewModel[], filter:any): boolean => {
      if (filter === undefined || filter === null || filter.toString().trim() === '') {
        return true;
      }
      if (!value || !value.length) {
        return false;
      }

      const containsFilter = (crimeGuns: ServerModels.IRCrimeGunViewModel) => crimeGuns.CrimeGunNumber.search(filter) !== -1;
      return value.some(containsFilter);
    });

    this.filterService.register('triageData', (value: ServerModels.IRCrimeGunViewModel[], filter:any): boolean => {
      if (filter === undefined || filter === null || filter.toString().trim() === '') {
        return true;
      }
      if (!value || !value.length) {
        return false;
      }

      const containsFilter = (triage: ServerModels.IRCrimeGunViewModel) => {
        if (triage === undefined || triage == null || triage.MostRecentTriageDate === undefined || triage.MostRecentTriageDate == null) {
          return false;
        }
        let dateString = triage.MostRecentTriageDate.toString();
        if (dateString.indexOf('Z') === -1) { 
          dateString = moment.parseZone(dateString).utc().format();
        }
        dateString = this.datePipe.transform(dateString, 'MM/dd/yyyy');
        return (triage.MostRecentTriageLevel.toLowerCase() + dateString).search(filter.toLowerCase()) !== -1;
      };

      return value.some(containsFilter);
    });

    this.filterService.register('personnel', (value: ServerModels.IRAssignedUsersViewModel[], filter:any): boolean => {
      if (filter === undefined || filter === null || filter.toString().trim() === '') {
        return true;
      }
      if (!value || !value.length) {
        return false;
      }

      const containsFilter = (personnel: ServerModels.IRAssignedUsersViewModel) => {
        if (personnel === undefined || personnel == null) {
          return false;
        }
        let searchString = personnel.FirstName?.toLowerCase() + personnel.LastName?.toLowerCase();
        return searchString.search(filter?.toLowerCase()) !== -1;
      };

      return value.some(containsFilter);
    });

    this.filterService.register('success', (value: ServerModels.ENESS_XREF_SuccessStoriesCIPs[], filter:any): boolean => {
      if (filter === undefined || filter === null || filter.toString().trim() === '') {
        return true;
      }
      if (!value || !value.length) {
        return false;
      }
      const containsFilter = (successStory: ServerModels.ENESS_XREF_SuccessStoriesCIPs) => successStory.ENESS_SuccessStory.InvestigativeTrackingNumber.search(filter.toUpperCase()) !== -1;

      return value.some(containsFilter);
    });
  }

  public applyFilters(filters: FilterModel) {
    this._clearLoadingStatus();
    this.infoReports = [];
    this.messageTemplate = 'Loading information reports...';
    this.dataTable.reset();
    this.getInfoReports(0, filters);
  }

  private _clearLoadingStatus(): void {
    this.loadingStartRow = 1;
    this.loadingEndRow = AppConstants.dbPagingDefaultRows;
    this.loadingStartRow$.next(this.loadingStartRow);
    this.loadingEndRow$.next(this.loadingEndRow);
  }

  public clearSidebar(): void {
    this.eventStream$.next(undefined);
    this.showSidebar = this.showInfoReportDetailView = this.showRelatedResultDetailView = false;
  }

  public getInfoReports(pageNumber: number, filters: FilterModel): void {
    // Include cgic code to retrieve IR for state and local user
    if (AccountComponent.userInfo.UserType.UserTypeName == 'StateLocalLE') {
      filters.cgicCode = this.cgicCode;
    }
    this.infoReportService.getInformationReportView(pageNumber, filters).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (irs: ServerModels.IntelligenceReportViewModel[]) => {
        this.loading = irs?.length > 0;
        if (irs?.length > 0) {
          let nextPage = pageNumber + 1;
          this.loadingStartRow = pageNumber == 0 ? 1 : (1 + this.infoReports.length);
          this.infoReports = this.infoReports.concat(irs);
          this.loadingEndRow = this.infoReports.length;
          this.loadingStartRow$.next(this.loadingStartRow);
          this.loadingEndRow$.next(this.loadingEndRow);
          this.messageTemplate = `Loaded rows ${this.loadingStartRow} thru ${this.loadingEndRow}...`;
          if (this.loading = irs.length == AppConstants.dbPagingDefaultRows) {
            this.getInfoReports(nextPage, filters);
          }
        }
      },
      (err:any) => {
        this.loading = false;
      }
    );
  }

  public showInfoReportDetails(data: ServerModels.IntelligenceReportViewModel): void {
    this.eventStream$.next(undefined);
    this.showInfoReportDetailView = this.showSidebar = true;
    this.selectedInfoReport$ = this.eventStream$.pipe(
      takeUntil(this.unsubscribe$),
      switchMap(() => this.infoReportService.getInformationReportById(data.IntelligenceReportID))
    );
  }

  public showResultDetails(result: ServerModels.ENESS_XREF_SuccessStoriesCIPs): void {
    this.selectedResultId = result.SuccessStoryID;
    this.showRelatedResultDetailView = this.showSidebar = true;
  }

  public newInfoReport(): void {
    this.previousRouteService.transferData = {
      ENESS_XREF_IRsCrimeGuns: [],
      ENESS_XREF_IRsLECaseNumbers: []
    } as Data;
    this.previousRouteService.returnUrl = this.router.url;
    this.router.navigate(['/info-report-form']);
  }

  public onShowSidebar(): void {
    window.setTimeout(() => {
      let targetEl = this.hostElement.nativeElement.getElementsByClassName('ui-sidebar-active');
      if (targetEl.length) {
        targetEl[0].scrollTop = 0;
        targetEl[0].setAttribute("tabindex", "-1");
        targetEl[0].focus();
      }
    }, 1000);
  }

  public getFilterValue(filter: any): any {
    if (!filter) {
      return null;
    }
    return (<FilterMetadata>filter).value;
  }

  public openRelatedCrimeGun(evt: Event, id: string): void {
    evt.preventDefault();
    const token = sessionStorage.getItem(environment.authTokenStorageKey);
    let url = (<HTMLLinkElement>evt.target).baseURI + '/crimegun/' + id;
    let windowObjRef = window.open(url, '_blank');
    windowObjRef.sessionStorage.setItem(environment.authTokenStorageKey, token);
  }

  public canViewIR(ir: ServerModels.IntelligenceReportViewModel): AuthorizationByStatusModel {
    if (this.canView) {
      this.canView.assigneeIds = ir.AssignedCGICPersonnel?.map(x => x.UserId);
    }
    return this._canViewIR;
  }

  public formatAssignedPersonnel(assignees: ServerModels.IRAssignedUsersViewModel[]): string[] {
    return assignees.map(a => `${a.FirstName} ${a.LastName}${a.IsPrimary ? ' (P)' : ''}`);
  }

  public customFilter(filterFieldName: string, targetField: string, filterValue: any) {
    this.dataTable.filteredValue = [];
    for (let i = 0; i < this.dataTable.value.length; i++) {
      let localMatch = true;
      let localFiltered = false;

      for (let prop in this.dataTable.filters) {
        if (this.dataTable.filters.hasOwnProperty(prop) && prop !== 'global') {
          localFiltered = true;
          let filterField = prop;
          let filterMeta = <FilterMetadata>this.dataTable.filters[filterField];

          if (filterField === filterFieldName) {
            filterMeta.value = filterValue;
            localMatch = this._executeLocalFilter(targetField, this.dataTable.value[i], filterMeta);
          }
          if (!localMatch) {
            break;
          }
        }
      }

      let matches: boolean = localFiltered && localMatch;
      if (matches) {
        this.dataTable.filteredValue.push(this.dataTable.value[i]);
      }
    }
    if (this.dataTable.filteredValue.length === this.dataTable.value.length) {
      this.dataTable.filteredValue = null;
    }

    if (this.dataTable.paginator) {
      this.dataTable.totalRecords = this.dataTable.filteredValue ? this.dataTable.filteredValue.length : this.dataTable.value ? this.dataTable.value.length : 0;
    }
  }

  private _executeLocalFilter(field: string, rowData: any, filterMeta: FilterMetadata): boolean {
    let filterValue = filterMeta.value;
    let filterMatchMode = filterMeta.matchMode || FilterMatchMode.STARTS_WITH;
    let dataFieldValue = AppUtils.resolveFieldData(rowData, field);
    let filterConstraint = this.filterService.filters[filterMatchMode];

    return filterConstraint(dataFieldValue, filterValue, this.dataTable.filterLocale);
  }

  public filterGlobal(value: any, field: string, matchMode: string) {
    if (this.dataTable.filterTimeout) {
      clearTimeout(this.dataTable.filterTimeout);
    }
    if (!this.dataTable.isFilterBlank(value)) {
      this.dataTable.filters[field] = { value: value, matchMode: matchMode };
    } else if (this.dataTable.filters[field]) {
      delete this.dataTable.filters[field];
    }

    this.dataTable.filterTimeout = setTimeout(() => {
      
      let globalFilterFieldsArray = this.dataTable.globalFilterFields || this.dataTable.columns;
      this.dataTable.filteredValue = [];

      for (let i = 0; i < this.dataTable.value.length; i++) {
        let globalMatch = false;

        if (this.dataTable.filters['global'] && !globalMatch && globalFilterFieldsArray) {
          for (let j = 0; j < globalFilterFieldsArray.length; j++) {
            let globalFilterField = globalFilterFieldsArray[j].field || globalFilterFieldsArray[j];
            switch (globalFilterField) {
              case 'CreatedOn':
                globalMatch = this.filterService.filters['date'](AppUtils.resolveFieldData(this.dataTable.value[i], globalFilterField), (<FilterMetadata>this.dataTable.filters['global']).value, this.dataTable.filterLocale);
                break;
              case 'DateSent':
                globalMatch = this.filterService.filters['date'](AppUtils.resolveFieldData(this.dataTable.value[i], globalFilterField), (<FilterMetadata>this.dataTable.filters['global']).value, this.dataTable.filterLocale);
                break;
              case 'PrimaryCrimeGunNumber':
                globalMatch = this.filterService.filters['crimeGunNumber'](AppUtils.resolveFieldData(this.dataTable.value[i], globalFilterField), (<FilterMetadata>this.dataTable.filters['global']).value, this.dataTable.filterLocale);
                break;
              case 'MostRecentTriageData':
                let filterMetaTriage = <FilterMetadata>this.dataTable.filters[globalFilterField];
                filterMetaTriage.value = (<FilterMetadata>this.dataTable.filters['global']).value;
                globalMatch = this._executeLocalFilter('PrimaryCrimeGunNumber', this.dataTable.value[i], filterMetaTriage);
                break;
              case 'AssignedCGICPersonnel':
                let filterMetaPersonnel = <FilterMetadata>this.dataTable.filters[globalFilterField];
                filterMetaPersonnel.value = (<FilterMetadata>this.dataTable.filters['global']).value;
                globalMatch = this._executeLocalFilter('AssignedCGICPersonnel', this.dataTable.value[i], filterMetaPersonnel);
                break;
              case 'ENESS_XREF_SuccessStoriesResults':
                globalMatch = this.filterService.filters['success'](AppUtils.resolveFieldData(this.dataTable.value[i], globalFilterField), (<FilterMetadata>this.dataTable.filters['global']).value, this.dataTable.filterLocale);
                break;
              default:
                globalMatch = this.filterService.filters[(<FilterMetadata>this.dataTable.filters['global']).matchMode](AppUtils.resolveFieldData(this.dataTable.value[i], globalFilterField), (<FilterMetadata>this.dataTable.filters['global']).value, this.dataTable.filterLocale);
                break;
            }
              
            if (globalMatch) {
              break;
            }
          }
            
          if (globalMatch) {
            this.dataTable.filteredValue.push(this.dataTable.value[i]);
          }
        }
      }


      if (this.dataTable.filteredValue.length == 0) this.dataTable.filteredValue = null;

      if (this.dataTable.paginator) {
        this.dataTable.totalRecords = this.dataTable.filteredValue ? this.dataTable.filteredValue.length : this.dataTable.value ? this.dataTable.value.length : 0;
      }

      this.dataTable.filterTimeout = null;
    }, this.dataTable.filterDelay);

    this.dataTable.anchorRowIndex = null;
  }

  public customSort(sortEvent: SortEvent) {
    let sortField: string = sortEvent.field;
    const sortFunction = (data1:any, data2:any) => {
      let value1 = null;
      let value2 = null;

      if (sortField.toLowerCase() == 'assignedcgicpersonnel') {
        value1 = data1['AssignedCGICPersonnel'].flatMap((f:any) => f.FirstName + f.LastName).join("");
        value2 = data2['AssignedCGICPersonnel'].flatMap((f:any) => f.FirstName + f.LastName).join("");
      } else if (sortField.toLowerCase() == 'mostrecenttriagedata') {
        value1 = data1['PrimaryCrimeGunNumber']?.flatMap((f:any) => f.MostRecentTriageLevel + f.MostRecentTriageDate.toString()).join("");
        value2 = data2['PrimaryCrimeGunNumber']?.flatMap((f:any) => f.MostRecentTriageLevel + f.MostRecentTriageDate.toString()).join("");
      } else if (sortField.toLowerCase() == 'eness_xref_successstoriesresults') {
        value1 = data1['ENESS_XREF_SuccessStoriesResults']?.flatMap((f:any) => f.ENESS_SuccessStory.InvestigativeTrackingNumber).join("");
        value2 = data2['ENESS_XREF_SuccessStoriesResults']?.flatMap((f:any) => f.ENESS_SuccessStory.InvestigativeTrackingNumber).join("");
      } else {
        value1 = AppUtils.resolveFieldData(data1, sortField);
        value2 = AppUtils.resolveFieldData(data2, sortField);
      }
      let result = null;

      if (value1 == null && value2 != null)
        result = -1;
      else if (value1 != null && value2 == null)
        result = 1;
      else if (value1 == null && value2 == null)
        result = 0;
      else if (typeof value1 === 'string' && typeof value2 === 'string')
        result = value1.localeCompare(value2);
      else
        result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;

      return (sortEvent.order * result);
    };

    sortEvent.data.sort(sortFunction);
  }

}
